import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

// Toast = ephemeral message in the bottom-right corner. Used for save
// confirmations, error notifications, join-success etc. Replaces
// inline error banners that block layout.

export type ToastKind = "success" | "error" | "info";

interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastContextValue {
  push: (_kind: ToastKind, _message: string) => void;
  success: (_message: string) => void;
  error: (_message: string) => void;
  info: (_message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const DURATION_MS = 4000;
const ANIMATE_OUT_MS = 200;

const FONT =
  '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

const kindColors: Record<ToastKind, { bg: string; bd: string; fg: string; icon: string }> = {
  success: {
    bg: "linear-gradient(135deg, rgba(16,185,129,0.22) 0%, rgba(16,185,129,0.08) 100%)",
    bd: "rgba(16,185,129,0.55)",
    fg: "#6ee7b7",
    icon: "✓",
  },
  error: {
    bg: "linear-gradient(135deg, rgba(239,68,68,0.22) 0%, rgba(239,68,68,0.08) 100%)",
    bd: "rgba(239,68,68,0.55)",
    fg: "#fca5a5",
    icon: "✕",
  },
  info: {
    bg: "linear-gradient(135deg, rgba(99,102,241,0.22) 0%, rgba(34,211,238,0.08) 100%)",
    bd: "rgba(99,102,241,0.55)",
    fg: "#a5b4fc",
    icon: "ℹ",
  },
};

interface ToastItemProps {
  toast: Toast;
  onDismiss: (_id: number) => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onDismiss }) => {
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLeaving(true);
      setTimeout(() => onDismiss(toast.id), ANIMATE_OUT_MS);
    }, DURATION_MS);
    return () => clearTimeout(timer);
  }, [toast.id, onDismiss]);

  const c = kindColors[toast.kind];
  return (
    <div
      role="status"
      aria-live="polite"
      onClick={() => {
        setLeaving(true);
        setTimeout(() => onDismiss(toast.id), ANIMATE_OUT_MS);
      }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "12px 14px",
        background: c.bg,
        border: `1px solid ${c.bd}`,
        borderRadius: "12px",
        color: "white",
        fontSize: "14px",
        fontFamily: FONT,
        backdropFilter: "blur(12px)",
        boxShadow: "0 20px 50px -20px rgba(0,0,0,0.5)",
        cursor: "pointer",
        minWidth: "240px",
        maxWidth: "360px",
        opacity: leaving ? 0 : 1,
        transform: leaving ? "translateX(20px)" : "translateX(0)",
        transition: `opacity ${ANIMATE_OUT_MS}ms ease, transform ${ANIMATE_OUT_MS}ms ease`,
      }}
    >
      <span
        style={{
          color: c.fg,
          fontSize: "18px",
          fontWeight: 800,
          lineHeight: 1,
          flexShrink: 0,
        }}
      >
        {c.icon}
      </span>
      <span style={{ flex: 1, lineHeight: 1.4 }}>{toast.message}</span>
    </div>
  );
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((kind: ToastKind, message: string) => {
    setToasts((prev) => [
      ...prev,
      { id: Date.now() + Math.random(), kind, message },
    ]);
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const value: ToastContextValue = {
    push,
    success: (m) => push("success", m),
    error: (m) => push("error", m),
    info: (m) => push("info", m),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Toast stack — fixed bottom-right, stacks downward. On mobile
          the stack centers and respects safe-area-inset. */}
      <div
        style={{
          position: "fixed",
          right: "16px",
          bottom: "16px",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          zIndex: 2000,
          pointerEvents: "none",
        }}
      >
        {toasts.map((t) => (
          <div key={t.id} style={{ pointerEvents: "auto" }}>
            <ToastItem toast={t} onDismiss={dismiss} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

/**
 * Hook for any component to push a toast.
 *   const toast = useToast();
 *   toast.success("Tipp gespeichert");
 *   toast.error("Speichern fehlgeschlagen");
 *
 * Throws if used outside <ToastProvider> — catches misuse early.
 */
export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used inside <ToastProvider>");
  }
  return ctx;
};
